/**
 * Crash-safe wrappers around Web Storage (`sessionStorage` / `localStorage`).
 *
 * WebKit/Safari can make `window.sessionStorage` / `window.localStorage` itself
 * `null` (or throw `SecurityError` on access) when storage is blocked — e.g.
 * "Block All Cookies", Private Browsing, Lockdown Mode, or a storage-restricted
 * iframe. Writes can also throw `QuotaExceededError`. Calling `.getItem` on a
 * `null` storage produced SEAR-526:
 *   "null is not an object (evaluating 'sessionStorage.getItem')".
 *
 * These wrappers expose the same `getItem/setItem/removeItem/clear` surface the
 * app already uses, so call sites need no try/catch of their own. Every access is
 * guarded against (a) the storage object being `null`, (b) `SecurityError` on
 * access, (c) `QuotaExceededError` on write, and (d) SSR (`window` undefined).
 * When the real storage is unavailable, an in-memory fallback keeps reads/writes
 * consistent within the page session so feature logic still behaves sanely.
 */

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'>;

const createMemoryStorage = (): StorageLike => {
	const store = new Map<string, string>();
	return {
		getItem: (key) => (store.has(key) ? (store.get(key) as string) : null),
		setItem: (key, value) => {
			store.set(key, String(value));
		},
		removeItem: (key) => {
			store.delete(key);
		},
		clear: () => {
			store.clear();
		},
	};
};

const createSafeStorage = (getStorage: () => Storage | null | undefined): StorageLike => {
	// Single shared fallback per storage so values written while blocked stay
	// readable for the lifetime of the page.
	const memory = createMemoryStorage();

	// Returns the real storage when usable, otherwise the in-memory fallback.
	// Resolving lazily (rather than once at module load) handles environments
	// where availability can differ between calls and avoids throwing at import.
	const resolve = (): StorageLike => {
		try {
			return getStorage() ?? memory;
		} catch {
			// Accessing the property itself can throw SecurityError in some WebKit modes.
			return memory;
		}
	};

	return {
		getItem: (key) => {
			try {
				return resolve().getItem(key);
			} catch {
				return memory.getItem(key);
			}
		},
		setItem: (key, value) => {
			try {
				resolve().setItem(key, value);
			} catch {
				// Two distinct failure modes reach here, with different read-back guarantees:
				// - SecurityError (storage null/blocked): resolve() always throws and returns
				//   `memory`, so a later getItem() reads back this value — consistent for the session.
				// - QuotaExceededError (real storage present but full): resolve() still returns the
				//   underlying storage, so getItem() reads from it and won't see this write — the
				//   value is orphaned in `memory`. The write is best-effort; we keep it to avoid
				//   silently dropping it, accepting it may be lost for later reads.
				memory.setItem(key, value);
			}
		},
		removeItem: (key) => {
			try {
				resolve().removeItem(key);
			} catch {
				memory.removeItem(key);
			}
		},
		clear: () => {
			try {
				resolve().clear();
			} catch {
				memory.clear();
			}
		},
	};
};

export const safeSessionStorage: StorageLike = createSafeStorage(() =>
	typeof window === 'undefined' ? null : window.sessionStorage
);

export const safeLocalStorage: StorageLike = createSafeStorage(() =>
	typeof window === 'undefined' ? null : window.localStorage
);
